import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import './Courses.css';

export default function Courses() {
  const { slug } = useParams();

  if (slug) {
    return <CourseDetail slug={slug} />;
  }

  return <CourseCatalogue />;
}

function CourseCatalogue() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get('/courses')
      .then((res) => setCourses(res.data.courses || []))
      .catch(() => setError('Unable to load courses right now. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="courses-page">
      <section className="courses-hero">
        <div className="container courses-hero-inner">
          <span className="eyebrow">Global Mind Wellness Centre</span>
          <h1>Learn. Reflect. Grow.</h1>
          <p>
            Short, practical self-paced courses designed to build useful
            knowledge and skills for healthier helping relationships.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="courses-section-heading">
            <div>
              <span className="eyebrow">Online Learning</span>
              <h2>Courses</h2>
            </div>
            <p>Learn at your own pace and earn a certificate when you pass.</p>
          </div>

          {loading && <p>Loading courses…</p>}
          {error && <div className="alert alert-error">{error}</div>}

          {!loading && !error && !courses.length && (
            <div className="card course-empty">
              <h3>Courses are coming soon</h3>
              <p>Our first learning programme is being prepared.</p>
            </div>
          )}

          <div className="course-grid">
            {courses.map((course) => (
              <article className="card course-card" key={course.id}>
                <div className="course-card-top">
                  <span className="course-badge">{course.level}</span>
                  {Number(course.price) === 0 && (
                    <span className="course-free">Free</span>
                  )}
                </div>

                <h3>{course.title}</h3>
                <p className="course-short-description">{course.shortDescription}</p>

                <div className="course-meta">
                  <span>{course.durationMinutes} min</span>
                  <span>{course.modules?.length || 0} modules</span>
                  {course.certificateEnabled && <span>Certificate</span>}
                </div>

                <Link
                  to={`/courses/${course.slug}`}
                  className="btn btn-primary course-card-button"
                >
                  View Course
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function CourseDetail({ slug }) {
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progressLoading, setProgressLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [assessmentResult, setAssessmentResult] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError('');

    apiClient.get(`/courses/${slug}`)
      .then((res) => setCourse(res.data.course))
      .catch(() => setError('Unable to load this course.'))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!user) {
      setProgress(null);
      return;
    }

    setProgressLoading(true);

    apiClient.get(`/courses/${slug}/progress`)
      .then((res) => setProgress(res.data.enrollment))
      .catch((err) => {
        if (err.response?.status !== 404) {
          setError('Unable to load your course progress.');
        }
        setProgress(null);
      })
      .finally(() => setProgressLoading(false));
  }, [slug, user]);

  const completedModules = useMemo(
    () => new Set(progress?.completedModules || []),
    [progress]
  );

  const allModulesComplete =
    !!course &&
    course.modules?.length > 0 &&
    course.modules.every((module) => completedModules.has(module.id));

  const certificate =
    assessmentResult?.certificate ||
    progress?.certificate ||
    null;

  async function enroll() {
    setActionLoading('enroll');
    setError('');

    try {
      const res = await apiClient.post(`/courses/${slug}/enroll`);
      setProgress(res.data.enrollment);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to enroll in this course.');
    } finally {
      setActionLoading('');
    }
  }

  async function completeModule(moduleId) {
    setActionLoading(moduleId);
    setError('');

    try {
      const res = await apiClient.post(
        `/courses/${slug}/modules/${moduleId}/complete`
      );

      setProgress((current) => ({
        ...(current || {}),
        completedModules: res.data.completedModules,
        currentModule: res.data.currentModule,
        status: res.data.status,
      }));
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to mark the module complete.');
    } finally {
      setActionLoading('');
    }
  }

  function setAnswer(questionId, optionIndex) {
    setAnswers((current) => ({
      ...current,
      [questionId]: optionIndex,
    }));
  }

  async function submitAssessment(event) {
    event.preventDefault();
    setActionLoading('assessment');
    setError('');
    setAssessmentResult(null);

    try {
      const res = await apiClient.post(`/courses/${slug}/assessment`, { answers });
      setAssessmentResult(res.data);

      if (res.data.certificate) {
        setProgress((current) => ({
          ...(current || {}),
          status: res.data.status,
          bestScore: res.data.bestScore,
          certificate: res.data.certificate,
        }));
      } else {
        setProgress((current) => ({
          ...(current || {}),
          status: res.data.status,
          bestScore: res.data.bestScore,
        }));
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to submit the assessment.');
    } finally {
      setActionLoading('');
    }
  }

  if (loading) {
    return (
      <div className="courses-page">
        <div className="container course-loading">Loading course…</div>
      </div>
    );
  }

  if (error && !course) {
    return (
      <div className="courses-page">
        <div className="container course-loading">
          <div className="alert alert-error">{error}</div>
          <Link to="/courses" className="btn btn-secondary">Back to Courses</Link>
        </div>
      </div>
    );
  }

  if (!course) return null;

  return (
    <div className="courses-page">
      <section className="course-detail-hero">
        <div className="container">
          <Link to="/courses" className="course-back-link">← All Courses</Link>

          <div className="course-detail-hero-grid">
            <div>
              <span className="eyebrow">Self-Paced Course</span>
              <h1>{course.title}</h1>
              <p className="course-detail-description">{course.description}</p>

              <div className="course-detail-meta">
                <span>{course.durationMinutes} minutes</span>
                <span>{course.modules.length} modules</span>
                <span>{course.level}</span>
                {course.certificateEnabled && <span>Certificate included</span>}
              </div>
            </div>

            <div className="card course-enrol-card">
              <div className="course-price">
                {Number(course.price) === 0 ? 'Free' : `${course.currency} ${course.price}`}
              </div>

              <p>
                Complete all modules and achieve at least {course.passMark}% in
                the final assessment to earn your certificate.
              </p>

              {!user ? (
                <div className="course-auth-actions">
                  <Link to="/login" className="btn btn-primary">Log in to Enrol</Link>
                  <Link to="/register" className="course-register-link">Create an account</Link>
                </div>
              ) : progress ? (
                <div className="course-progress-summary">
                  <strong>
                    {progress.status === 'completed'
                      ? 'Course completed'
                      : `${progress.completedModules?.length || 0} of ${course.modules.length} modules complete`}
                  </strong>
                  {progress.bestScore !== null && progress.bestScore !== undefined && (
                    <span>Best assessment score: {progress.bestScore}%</span>
                  )}
                </div>
              ) : Number(course.price) > 0 ? (
                <Link
                  to={`/pay/course/${course.id}`}
                  className="btn btn-primary"
                >
                  Enrol & Pay
                </Link>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={enroll}
                  disabled={actionLoading === 'enroll' || progressLoading}
                >
                  {actionLoading === 'enroll' ? 'Enrolling…' : 'Start Course'}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          {error && <div className="alert alert-error">{error}</div>}

          {certificate && (
            <CertificateCard certificate={certificate} />
          )}

          {assessmentResult && !assessmentResult.attempt?.passed && (
            <div className="card assessment-result assessment-failed">
              <span className="eyebrow">Assessment Result</span>
              <h2>Keep going</h2>
              <p>
                You scored <strong>{assessmentResult.attempt.score}%</strong>.
                The pass mark is {assessmentResult.passMark}%.
              </p>
              <p>You can review the modules and attempt the assessment again.</p>
            </div>
          )}

          <div className="course-modules-heading">
            <div>
              <span className="eyebrow">Course Content</span>
              <h2>Five practical modules</h2>
            </div>
            {!user && (
              <p>Read through the course content before deciding whether to enrol.</p>
            )}
          </div>

          <div className="course-modules">
            {course.modules.map((module, index) => {
              const completed = completedModules.has(module.id);

              return (
                <article
                  className={`card course-module ${completed ? 'completed' : ''}`}
                  key={module.id}
                >
                  <div className="module-number">{String(index + 1).padStart(2, '0')}</div>

                  <div className="module-content">
                    <div className="module-heading">
                      <div>
                        <h3>{module.title}</h3>
                        <p>{module.description}</p>
                      </div>
                      <span className="module-duration">{module.durationMinutes} min</span>
                    </div>

                    <div className="module-reading">
                      <ReactMarkdown>{module.content}</ReactMarkdown>
                    </div>

                    {user && progress && (
                      <div className="module-action">
                        {completed ? (
                          <span className="module-complete">✓ Module completed</span>
                        ) : (
                          <button
                            className="btn btn-secondary"
                            onClick={() => completeModule(module.id)}
                            disabled={actionLoading === module.id}
                          >
                            {actionLoading === module.id ? 'Saving…' : 'Mark Module Complete'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          {user && progress && allModulesComplete && !certificate && (
            <Assessment
              course={course}
              answers={answers}
              setAnswer={setAnswer}
              onSubmit={submitAssessment}
              loading={actionLoading === 'assessment'}
            />
          )}

          {user && !progress && !progressLoading && (
            <div className="card course-enrol-prompt">
              <span className="eyebrow">Ready when you are</span>
              <h2>Take the course at your own pace</h2>
              <p>
                {Number(course.price) > 0
                  ? `Enrol for ${course.currency} ${course.price} to track your progress, complete the modules and take the final assessment.`
                  : 'Enrol for free to track your progress, complete the modules and take the final assessment.'}
              </p>
              {Number(course.price) > 0 ? (
                <Link
                  to={`/pay/course/${course.id}`}
                  className="btn btn-primary"
                >
                  Enrol & Pay
                </Link>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={enroll}
                  disabled={actionLoading === 'enroll'}
                >
                  {actionLoading === 'enroll' ? 'Enrolling…' : 'Start Course'}
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Assessment({ course, answers, setAnswer, onSubmit, loading }) {
  const answeredCount = Object.keys(answers).length;
  const total = course.questions.length;

  return (
    <section className="assessment-section">
      <div className="assessment-intro">
        <span className="eyebrow">Final Assessment</span>
        <h2>Check your understanding</h2>
        <p>
          Answer all {total} questions. You need at least {course.passMark}% to
          pass and receive your certificate.
        </p>
      </div>

      <form className="assessment-form" onSubmit={onSubmit}>
        {course.questions.map((question, index) => (
          <fieldset className="card assessment-question" key={question.id}>
            <legend>
              <span>{index + 1}</span>
              {question.question}
            </legend>

            <div className="assessment-options">
              {question.options.map((option, optionIndex) => (
                <label
                  className={`assessment-option ${
                    Number(answers[question.id]) === optionIndex ? 'selected' : ''
                  }`}
                  key={optionIndex}
                >
                  <input
                    type="radio"
                    name={question.id}
                    value={optionIndex}
                    checked={Number(answers[question.id]) === optionIndex}
                    onChange={() => setAnswer(question.id, optionIndex)}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </fieldset>
        ))}

        <div className="assessment-submit">
          <span>{answeredCount} of {total} answered</span>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || answeredCount !== total}
          >
            {loading ? 'Submitting…' : 'Submit Assessment'}
          </button>
        </div>
      </form>
    </section>
  );
}

function CertificateCard({ certificate }) {
  const verificationPath = `/certificate/${certificate.verificationToken}`;

  return (
    <section className="certificate-section">
      <div className="certificate-card">
        <div className="certificate-inner">
          <div className="certificate-kicker">GLOBAL MIND WELLNESS CENTRE</div>
          <div className="certificate-rule" />

          <p className="certificate-label">CERTIFICATE OF COMPLETION</p>

          <h2>Presented to</h2>
          <div className="certificate-recipient">{certificate.recipientName}</div>

          <p className="certificate-text">
            for successfully completing
          </p>

          <div className="certificate-course">{certificate.courseTitle}</div>

          <p className="certificate-text">
            This certificate confirms successful completion of the course
            requirements and final assessment.
          </p>

          <div className="certificate-footer">
            <div className="certificate-signature-block">
              <div className="certificate-signature">Susan Amdany</div>
              <div className="certificate-signature-line" />
              <strong>{certificate.signerName}</strong>
              <span>{certificate.signerTitle}</span>
            </div>

            <div className="certificate-details">
              <strong>{certificate.certificateNumber}</strong>
              <span>
                Issued {new Date(certificate.issuedAt).toLocaleDateString()}
              </span>
              <Link to={verificationPath}>Verify Certificate</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="certificate-actions">
        <button className="btn btn-secondary" onClick={() => window.print()}>
          Print / Save Certificate
        </button>
        <Link to={verificationPath} className="btn btn-outline certificate-verify-button">
          Open Verification
        </Link>
      </div>
    </section>
  );
}
